import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Transaction, TransactionStatus } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { mockDataService } from '@/services/mockDataService';
import { ArrowUpRight, ArrowDownRight, Printer, Download, Filter, Search, Plus, RefreshCw, DollarSign, Clock, List } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { DateRange } from 'react-day-picker';
import { subDays } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import { Progress } from '@/components/ui/progress';

const Transactions = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState('all');
  const [date, setDate] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Form state for creating a new transaction
  const [newTransaction, setNewTransaction] = useState({
    organization_id: '',
    customer_id: '',
    subscription_id: '',
    amount: 0,
    currency: 'BRL',
    payment_method: '',
    status: 'pending' as TransactionStatus,
    reference_number: '',
    description: '',
    payment_date: new Date().toISOString(),
  });

  // Fetch transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        setTimeout(() => {
          const mockTransactions = mockDataService.getTransactions();
          setTransactions(mockTransactions);
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        toast.error('Failed to fetch transactions');
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewTransaction({
      ...newTransaction,
      [name]: name === 'amount' ? parseFloat(value) : value,
    });
  };

  // Handle select changes
  const handleSelectChange = (name: string, value: string) => {
    setNewTransaction({
      ...newTransaction,
      [name]: value,
    });
  };

  // Handle create transaction
  const handleCreateTransaction = async () => {
    try {
      const newTransactionData = mockDataService.createTransaction(newTransaction);
      setTransactions([newTransactionData, ...transactions]);
      setIsCreateDialogOpen(false);
      setNewTransaction({
        organization_id: '',
        customer_id: '',
        subscription_id: '',
        amount: 0,
        currency: 'BRL',
        payment_method: '',
        status: 'pending',
        reference_number: '',
        description: '',
        payment_date: new Date().toISOString(),
      });
      toast.success('Transaction created successfully');
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error('Failed to create transaction');
    }
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  // Calculate summary data
  const getSummaryData = () => {
    const completed = transactions.filter(t => t.status === 'completed');
    const pending = transactions.filter(t => t.status === 'pending');
    const failed = transactions.filter(t => t.status === 'failed');
    const refunded = transactions.filter(t => t.status === 'refunded');
    
    const totalAmount = completed.reduce((sum, t) => sum + t.amount, 0);
    const pendingAmount = pending.reduce((sum, t) => sum + t.amount, 0);
    const refundedAmount = refunded.reduce((sum, t) => sum + t.amount, 0);
    
    return {
      totalCount: transactions.length,
      completedCount: completed.length,
      pendingCount: pending.length,
      failedCount: failed.length,
      refundedCount: refunded.length,
      totalAmount,
      pendingAmount,
      refundedAmount,
    };
  };

  // Filter transactions based on search term, status and date
  const getFilteredTransactions = () => {
    return transactions.filter((transaction) => {
      const matchesSearch =
        searchTerm === '' ||
        transaction.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.payment_method.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.customer_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.subscription_id?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;

      const matchesTab = 
        activeTab === 'all' || 
        (activeTab === 'income' && transaction.status === 'completed') ||
        (activeTab === 'pending' && transaction.status === 'pending') ||
        (activeTab === 'refunded' && transaction.status === 'refunded');

      const transactionDate = transaction.payment_date 
        ? new Date(transaction.payment_date) 
        : new Date(transaction.created_at);
      
      const matchesDateRange = 
        !date?.from || !date?.to || 
        (transactionDate >= date.from && transactionDate <= date.to);

      return matchesSearch && matchesStatus && matchesTab && matchesDateRange;
    });
  };

  const summaryData = getSummaryData();
  const filteredTransactions = getFilteredTransactions();

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Transações</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700">
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Transações</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Gerencie as transações financeiras da sua organização
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200">
                  <Download className="h-4 w-4 mr-2" /> Exportar
                </Button>
                <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg">
                  <Plus className="h-4 w-4 mr-2" /> Nova Transação
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="pl-3 lg:pl-4">
          <div className="container mx-auto px-6 -mt-4 relative z-10">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded-md">
                        <ArrowUpRight className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Total Recebido</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{formatCurrency(summaryData.totalAmount, 'BRL')}</div>
                  </div>
                  <p className="text-xs text-gray-500">{summaryData.completedCount} transações completadas</p>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-yellow-100 rounded-md">
                        <Clock className="h-4 w-4 text-yellow-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Pendente</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{formatCurrency(summaryData.pendingAmount, 'BRL')}</div>
                  </div>
                  <p className="text-xs text-gray-500">{summaryData.pendingCount} transações pendentes</p>
                </CardContent>
              </Card>
              
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-md">
                        <ArrowDownRight className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Reembolsado</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{formatCurrency(summaryData.refundedAmount, 'BRL')}</div>
                  </div>
                  <p className="text-xs text-gray-500">{summaryData.refundedCount} transações reembolsadas</p>
                </CardContent>
              </Card>
              
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-purple-100 rounded-md">
                        <List className="h-4 w-4 text-purple-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Total de Transações</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{summaryData.totalCount}</div>
                  </div>
                  <Progress value={summaryData.totalCount > 0 ? (summaryData.completedCount / summaryData.totalCount) * 100 : 0} className="h-1.5" />
                  <div className="text-xs text-gray-500 mt-2">
                    {summaryData.completedCount} de {summaryData.totalCount} completadas
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Card */}
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 text-muted-foreground -translate-y-1/2" />
                    <Input
                      placeholder="Buscar transações..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-10 bg-white"
                    />
                  </div>
                  
                  <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex gap-1 h-10 bg-white">
                        <Filter size={16} />
                        <span>Filtros</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <h4 className="font-extrabold">Período</h4>
                          <Calendar
                            mode="range"
                            selected={date}
                            onSelect={setDate}
                            className="rounded-md border"
                          />
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-extrabold">Status</h4>
                          <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger>
                              <SelectValue placeholder="Filtrar por status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos os status</SelectItem>
                              <SelectItem value="pending">Pendente</SelectItem>
                              <SelectItem value="completed">Concluído</SelectItem>
                              <SelectItem value="failed">Falhou</SelectItem>
                              <SelectItem value="refunded">Reembolsado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-between">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setStatusFilter('all');
                              setDate(undefined);
                            }}
                          >
                            Resetar
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => setIsFilterOpen(false)}
                          >
                            Aplicar Filtros
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
                  <TabsList>
                    <TabsTrigger value="all">Todas</TabsTrigger>
                    <TabsTrigger value="income">Recebidas</TabsTrigger>
                    <TabsTrigger value="pending">Pendentes</TabsTrigger>
                    <TabsTrigger value="refunded">Reembolsadas</TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardHeader>
              
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Referência</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Método</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-10">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            </div>
                            <div className="mt-2 text-sm text-muted-foreground">Carregando transações...</div>
                          </TableCell>
                        </TableRow>
                      ) : filteredTransactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-10">
                            <div className="text-sm text-muted-foreground">Nenhuma transação encontrada</div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredTransactions.map((transaction) => (
                          <TableRow key={transaction.id} className="cursor-pointer hover:bg-muted/50">
                            <TableCell className="font-extrabold">{transaction.reference_number}</TableCell>
                            <TableCell className="max-w-[200px] truncate">{transaction.description}</TableCell>
                            <TableCell className="font-extrabold">{formatCurrency(transaction.amount, transaction.currency)}</TableCell>
                            <TableCell>
                              {transaction.payment_method === 'credit_card' && 'Cartão de Crédito'}
                              {transaction.payment_method === 'debit_card' && 'Cartão de Débito'}
                              {transaction.payment_method === 'bank_transfer' && 'Transferência Bancária'}
                              {transaction.payment_method === 'paypal' && 'PayPal'}
                              {transaction.payment_method === 'pix' && 'PIX'}
                              {transaction.payment_method === 'boleto' && 'Boleto'}
                              {transaction.payment_method === 'apple_pay' && 'Apple Pay'}
                              {transaction.payment_method === 'google_pay' && 'Google Pay'}
                            </TableCell>
                            <TableCell>
                              {transaction.payment_date
                                ? format(new Date(transaction.payment_date), 'dd/MM/yyyy')
                                : format(new Date(transaction.created_at), 'dd/MM/yyyy')}
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                transaction.status === 'completed' ? 'success' :
                                transaction.status === 'pending' ? 'warning' :
                                transaction.status === 'failed' ? 'destructive' :
                                'info'
                              }>
                                {transaction.status === 'completed' && 'Concluído'}
                                {transaction.status === 'pending' && 'Pendente'}
                                {transaction.status === 'failed' && 'Falhou'}
                                {transaction.status === 'refunded' && 'Reembolsado'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Mostrando {filteredTransactions.length} de {transactions.length} transações
                </div>
              </CardContent>
            </Card>
            <div className="h-16" /> {/* Bottom spacing */}
          </div>
        </div>
      </div>

      {/* Dialog for creating a new transaction */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Criar Nova Transação</DialogTitle>
            <DialogDescription>
              Preencha os detalhes da transação abaixo.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="organization_id" className="text-right">
                Organização
              </Label>
              <Input
                id="organization_id"
                name="organization_id"
                value={newTransaction.organization_id}
                onChange={handleInputChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="customer_id" className="text-right">
                Cliente
              </Label>
              <Input
                id="customer_id"
                name="customer_id"
                value={newTransaction.customer_id}
                onChange={handleInputChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="subscription_id" className="text-right">
                Assinatura
              </Label>
              <Input
                id="subscription_id"
                name="subscription_id"
                value={newTransaction.subscription_id}
                onChange={handleInputChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="amount" className="text-right">
                Valor
              </Label>
              <Input
                id="amount"
                name="amount"
                type="number"
                value={newTransaction.amount}
                onChange={handleInputChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="currency" className="text-right">
                Moeda
              </Label>
              <Select
                value={newTransaction.currency}
                onValueChange={(value) => handleSelectChange('currency', value)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecione a moeda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BRL">Real (BRL)</SelectItem>
                  <SelectItem value="USD">Dólar (USD)</SelectItem>
                  <SelectItem value="EUR">Euro (EUR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="payment_method" className="text-right">
                Método
              </Label>
              <Select
                value={newTransaction.payment_method}
                onValueChange={(value) => handleSelectChange('payment_method', value)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecione o método" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit_card">Cartão de Crédito</SelectItem>
                  <SelectItem value="debit_card">Cartão de Débito</SelectItem>
                  <SelectItem value="bank_transfer">Transferência Bancária</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="apple_pay">Apple Pay</SelectItem>
                  <SelectItem value="google_pay">Google Pay</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                Status
              </Label>
              <Select
                value={newTransaction.status}
                onValueChange={(value) => handleSelectChange('status', value as TransactionStatus)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                  <SelectItem value="failed">Falhou</SelectItem>
                  <SelectItem value="refunded">Reembolsado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="reference_number" className="text-right">
                Referência
              </Label>
              <Input
                id="reference_number"
                name="reference_number"
                value={newTransaction.reference_number}
                onChange={handleInputChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Descrição
              </Label>
              <Textarea
                id="description"
                name="description"
                value={newTransaction.description}
                onChange={handleInputChange}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" onClick={handleCreateTransaction}>
              Criar Transação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Transactions;